import { useEffect, useState } from 'react';

export type EnterExitTransition = {
  mounted: boolean;
  active: boolean;
};

type Options = {
  /**
   * Defer flipping `active` to `true` until a double-`requestAnimationFrame`
   * after mounting, so the enter transition plays from the pre-mount state
   * rather than snapping straight to the entered state.
   */
  deferEnter?: boolean;
};

/**
 * Drives an enter/exit CSS transition off a boolean `show`.
 *
 * On enter it mounts immediately and (optionally) defers `active` by a
 * double-rAF so the transition runs. On exit it flips `active` off right away
 * and keeps the element mounted for `exitMs` so the exit transition can finish
 * before unmount.
 */
export const useEnterExitTransition = (
  show: boolean,
  exitMs: number,
  { deferEnter = false }: Options = {},
): EnterExitTransition => {
  const [mounted, setMounted] = useState(show);
  const [active, setActive] = useState(show);

  useEffect(() => {
    if (show) {
      setMounted(true);
      if (deferEnter) {
        let innerRaf = 0;
        const outerRaf = requestAnimationFrame(() => {
          innerRaf = requestAnimationFrame(() => setActive(true));
        });
        return () => {
          cancelAnimationFrame(outerRaf);
          cancelAnimationFrame(innerRaf);
        };
      }
      setActive(true);
      return;
    }
    setActive(false);
    const timer = setTimeout(() => setMounted(false), exitMs);
    return () => clearTimeout(timer);
  }, [show, exitMs, deferEnter]);

  return { mounted, active };
};
